import React, { useEffect, useRef, useState } from 'react';
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Volume2, Volume1, VolumeX, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Track } from "@shared/schema";

// Global audio player registry to handle single player at a time
type AudioPlayerRegistry = {
  currentPlayer: HTMLAudioElement | null;
  fadeOutAndPause: (audio: HTMLAudioElement) => Promise<void>;
}

// Create a registry that will track the currently playing audio element
const audioPlayerRegistry: AudioPlayerRegistry = {
  currentPlayer: null,
  
  // Fade out audio smoothly over 500ms before pausing
  fadeOutAndPause: async (audio: HTMLAudioElement) => {
    if (!audio) return;
    
    const originalVolume = audio.volume;
    const fadeSteps = 10;
    const fadeStepTime = 50; // 50ms per step = 500ms total
    
    // Gradually reduce volume
    for (let i = fadeSteps - 1; i >= 0; i--) {
      audio.volume = originalVolume * (i / fadeSteps);
      await new Promise(resolve => setTimeout(resolve, fadeStepTime));
    }
    
    // Pause the audio
    audio.pause();
    
    // Reset volume for next play
    audio.volume = originalVolume;
  }
};

interface AudioPlayerProps {
  src: string;
  trackId?: number;
  isPlaying: boolean;
  onPlayPause: () => void;
  onEnded?: () => void;
  className?: string;
  mini?: boolean;
  onLoadError?: (error: Error) => void;
}

const formatTime = (seconds: number): string => {
  if (isNaN(seconds)) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// First add this utility function at the top of the file, outside the component
function forcePlayAudio(audio: HTMLAudioElement, src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // Create a new Audio element (not connected to DOM) to test loading
    const testAudio = new Audio();
    
    // Set up event listeners
    const onCanPlay = () => {
      console.log("Test audio can play, applying to real player");
      
      // Apply to the real audio element
      audio.src = src;
      audio.load();
      
      // Clean up test audio
      testAudio.removeEventListener('canplay', onCanPlay);
      testAudio.removeEventListener('error', onError);
      testAudio.src = '';
      
      // Try to play the real audio
      audio.play()
        .then(resolve)
        .catch(reject);
    };
    
    const onError = (e: Event) => {
      console.error("Test audio failed to load:", (testAudio as any).error);
      testAudio.removeEventListener('canplay', onCanPlay);
      testAudio.removeEventListener('error', onError);
      reject(new Error("Audio format not supported"));
    };
    
    // Set up test audio
    testAudio.addEventListener('canplay', onCanPlay);
    testAudio.addEventListener('error', onError);
    
    // Start loading
    console.log("Testing audio load with test element:", src);
    testAudio.src = src;
    testAudio.load();
  });
}

// Function to fetch via proxy if direct S3 URL fails
async function fetchAudioViaProxy(trackId: number): Promise<string> {
  console.log(`Fetching track ${trackId} via proxy endpoint...`);
  
  // Use the direct file endpoint that proxies through our server
  const response = await fetch(`/api/tracks/${trackId}/file`);
  
  if (!response.ok) {
    throw new Error(`Proxy request failed with status ${response.status}`);
  }
  
  console.log(`Proxy response received with content-type: ${response.headers.get('content-type')}`);
  
  // Get the file as a blob
  const blob = await response.blob();
  
  // Create a blob URL
  const blobUrl = URL.createObjectURL(blob);
  console.log(`Created blob URL from proxy response: ${blobUrl}`);
  
  return blobUrl;
}

export function AudioPlayer({
  src,
  trackId,
  isPlaying,
  onPlayPause,
  onEnded,
  className,
  mini = false,
  onLoadError
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [audioType, setAudioType] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Fetch secure URL if trackId is provided
  const { data: secureUrlData, refetch: refetchUrl } = useQuery({
    queryKey: ['/api/tracks', trackId, 'secure-url'],
    queryFn: async () => {
      if (!trackId) return null;
      
      try {
        setIsLoadingUrl(true);
        const response = await fetch(`/api/tracks/${trackId}/secure-url`);
        if (!response.ok) throw new Error('Failed to fetch secure URL');
        const data = await response.json();
        
        // Log successful retrieval for debugging
        console.log(`Retrieved secure URL for track ${trackId} (format: ${data.format})`);
        
        return data;
      } catch (err) {
        console.error(`Error fetching secure URL for track ${trackId}:`, err);
        throw err;
      } finally {
        setIsLoadingUrl(false);
      }
    },
    enabled: !!trackId, // Only run query if trackId exists
    refetchInterval: 800000, // Refetch every ~13 minutes (as our URLs expire after 15 min)
  });

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      // Clear previous errors when audio loads successfully
      setLoadError(null);
    };

    const handleEnded = () => {
      if (onEnded) onEnded();
    };

    const handleError = (e: Event) => {
      const error = (e.target as HTMLAudioElement).error;
      console.error("Audio error:", error);
      setLoadError(error ? new Error(error.message) : new Error("Unknown audio error"));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [onEnded]);

  // Modified useEffect to preload audio
  useEffect(() => {
    if (secureUrlData?.secureUrl) {
      const audio = audioRef.current;
      if (!audio) return;

      // Store the content type for this audio
      if (secureUrlData.contentType) {
        setAudioType(secureUrlData.contentType);
      }
      
      // Preload the MP3 to check compatibility before setting it on the audio element
      console.log(`Preloading audio for track ${trackId}...`);
      
      // Set the audio source to the secure URL
      console.log(`Using secure URL for track ${trackId}`);
      console.log(`Content type: ${secureUrlData.contentType}`);
      console.log(`Format: ${secureUrlData.format}`);
      
      // Set the source
      audio.src = secureUrlData.secureUrl;
      
      // Force audio element to preload
      audio.preload = 'auto';
      audio.load(); // This is needed to apply the preload setting
      
      // Clear any previous errors
      setLoadError(null);
    }
  }, [secureUrlData, trackId]);
  
  // Now modify the play useEffect
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !secureUrlData?.secureUrl) return;

    if (isPlaying) {
      // If there's already another track playing, fade it out first
      if (audioPlayerRegistry.currentPlayer && audioPlayerRegistry.currentPlayer !== audio) {
        audioPlayerRegistry.fadeOutAndPause(audioPlayerRegistry.currentPlayer);
      }
      
      // Register current audio as the active player
      audioPlayerRegistry.currentPlayer = audio;
      
      // Record track play if we have a trackId
      if (trackId && audio.currentTime < 3) {
        fetch(`/api/tracks/${trackId}/play`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }).catch(error => console.error("Error recording track play:", error));
      }
      
      // Try to play with the new approach
      console.log(`Attempting to play audio for track ${trackId}...`);
      
      // First - Clean attempt with direct S3 URL
      audio.src = secureUrlData.secureUrl;
      audio.load();
      
      audio.play()
        .then(() => {
          console.log(`Successfully playing track ${trackId} directly`);
        })
        .catch(error => {
          console.error(`Direct playback failed for track ${trackId}:`, error);
          console.log(`Error name: ${error.name}, message: ${error.message}`);
          
          // Second - Try with test audio element
          console.log(`Trying with test audio element...`);
          forcePlayAudio(audio, secureUrlData.secureUrl)
            .then(() => {
              console.log(`Successfully playing track ${trackId} with test audio`);
            })
            .catch(error => {
              console.error(`Test audio method failed for track ${trackId}:`, error);
              
              // Third - Try with blob URL from direct fetch
              console.log(`Trying with blob URL...`);
              fetch(secureUrlData.secureUrl)
                .then(response => {
                  console.log(`Direct fetch response: ${response.status}, content-type: ${response.headers.get('content-type')}`);
                  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                  return response.blob();
                })
                .then(blob => {
                  const blobUrl = URL.createObjectURL(blob);
                  console.log(`Created blob URL: ${blobUrl}`);
                  audio.src = blobUrl;
                  audio.load();
                  return audio.play();
                })
                .then(() => {
                  console.log(`Successfully playing track ${trackId} with blob URL`);
                })
                .catch(error => {
                  console.error(`Blob URL method failed for track ${trackId}:`, error);
                  
                  // Fourth and final - Use proxy endpoint
                  console.log(`Trying proxy fallback...`);
                  if (!trackId) {
                    console.error(`Cannot use proxy fallback without trackId`);
                    setLoadError(new Error("All playback methods failed"));
                    return;
                  }
                  
                  fetchAudioViaProxy(trackId)
                    .then(proxyUrl => {
                      console.log(`Using proxy URL: ${proxyUrl}`);
                      audio.src = proxyUrl;
                      audio.load();
                      return audio.play();
                    })
                    .then(() => {
                      console.log(`Successfully playing track ${trackId} via proxy`);
                    })
                    .catch(finalError => {
                      console.error(`All playback methods failed for track ${trackId}:`, finalError);
                      setLoadError(finalError);
                    });
                });
            });
        });
    } else {
      audioPlayerRegistry.fadeOutAndPause(audio);
    }
  }, [isPlaying, trackId, secureUrlData]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  const handleProgressChange = (values: number[]) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    const newTime = values[0];
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (values: number[]) => {
    const newVolume = values[0];
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return <VolumeX className="h-4 w-4" />;
    if (volume < 0.5) return <Volume1 className="h-4 w-4" />;
    return <Volume2 className="h-4 w-4" />;
  };

  // Display loading/error states
  const renderStatusIndicator = () => {
    if (isLoadingUrl) {
      return (
        <div className="text-blue-500 text-xs flex items-center mt-1 gap-1">
          <AlertCircle className="h-3 w-3" /> 
          Loading audio...
        </div>
      );
    }
    
    if (loadError) {
      return (
        <div className="text-red-500 text-xs flex items-center mt-1 gap-1">
          <AlertCircle className="h-3 w-3" /> 
          {loadError.name === "NotSupportedError" 
            ? "Format not supported" 
            : "Error loading audio"}
        </div>
      );
    }
    return null;
  };

  // Add an enhanced error handler for audio elements
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    
    // Track load attempts
    let loadAttempts = 0;
    const maxAttempts = 3;
    
    const handleAudioError = async (e: Event) => {
      const error = (audio as HTMLAudioElement).error;
      console.error(`Audio error (${++loadAttempts}/${maxAttempts}):`, error);
      
      if (loadAttempts < maxAttempts && secureUrlData?.secureUrl) {
        // Try again with a short delay (helps with some intermittent issues)
        console.log(`Retry attempt ${loadAttempts}/${maxAttempts} for track ${trackId}...`);
        setTimeout(() => {
          // Create a fresh URL with cache-busting parameter
          const cacheBuster = `?cb=${Date.now()}`;
          const freshUrl = secureUrlData.secureUrl.includes('?') 
            ? `${secureUrlData.secureUrl}&cb=${Date.now()}` 
            : `${secureUrlData.secureUrl}${cacheBuster}`;
            
          console.log(`Trying with fresh URL and explicit MIME type`);
          audio.src = freshUrl;
          
          // Attempt to reload
          audio.load();
          if (isPlaying) {
            audio.play().catch(playError => {
              console.error(`Retry ${loadAttempts} play error:`, playError);
            });
          }
        }, 800 * loadAttempts); // Increasing delay with each attempt
      } else {
        // We've exhausted our retries or don't have a URL
        console.error(`Failed to play audio after ${loadAttempts} attempts`);
        setLoadError(new Error('Failed to play audio after multiple attempts'));
      }
    };
    
    // Add a one-time error listener that will be removed and recreated on dependency changes
    audio.addEventListener('error', handleAudioError);
    
    return () => {
      audio.removeEventListener('error', handleAudioError);
    };
  }, [secureUrlData, trackId, isPlaying]);

  // Propagate load errors to parent
  useEffect(() => {
    if (loadError && onLoadError) {
      onLoadError(loadError);
    }
  }, [loadError, onLoadError]);

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Audio element with our proxied source */}
      <audio 
        ref={audioRef} 
        preload="metadata"
      >
        Your browser does not support the audio element.
      </audio>
      
      {mini ? (
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <Slider
              value={[currentTime]}
              min={0}
              max={duration || 100}
              step={0.1}
              onValueChange={handleProgressChange}
              className="h-1"
            />
          </div>
          {renderStatusIndicator()}
        </div>
      ) : (
        <>
          <div className="flex justify-center mb-2">
            <button
              onClick={onPlayPause}
              className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white hover:bg-primary/90 transition-colors"
              disabled={isLoadingUrl}
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
            </button>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 min-w-[30px]">
              {formatTime(currentTime)}
            </span>
            <Slider
              value={[currentTime]}
              min={0}
              max={duration || 100}
              step={0.1}
              onValueChange={handleProgressChange}
              className="flex-1"
            />
            <span className="text-xs text-gray-500 min-w-[30px]">
              {formatTime(duration)}
            </span>
          </div>
          
          <div className="flex items-center mt-2 gap-2">
            <button onClick={toggleMute} className="text-gray-600 hover:text-primary">
              {getVolumeIcon()}
            </button>
            <Slider
              value={[volume]}
              min={0}
              max={1}
              step={0.01}
              onValueChange={handleVolumeChange}
              className="w-24"
            />
          </div>
          
          {renderStatusIndicator()}
        </>
      )}
    </div>
  );
}
